import { tableDateFormat } from "components/materials/page/helpers";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { ChatIcon, ScoreIcon } from "mds/icons";
import {
  PagesWithOutcomeAssessmentAndSectionType,
  PagesWithOutcomeAssessmentType,
} from "store/selectors/types";
import { Link } from "utils/routing";
import { pageUrl } from "utils/urls";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

type OutcomeAssessmentTableData =
  | PagesWithOutcomeAssessmentType
  | PagesWithOutcomeAssessmentAndSectionType;

interface OutcomeAssessmentTableProps {
  data: OutcomeAssessmentTableData[];
  title: string;
  showCourseColumn?: boolean;
}

/**
 * Displays a table of assessment data for pages with outcomes.
 * Can optionally show a course column for cross-course data.
 */
export const OutcomeAssessmentTable = ({
  data,
  title,
  showCourseColumn = false,
}: OutcomeAssessmentTableProps) => {
  return (
    <div className="assessment-dashboard-table pb-6">
      <h2 className="h3 m-0 text-black-tint-40">{title}</h2>
      <table className="mds-table">
        <thead>
          <tr>
            {showCourseColumn && <th className="th-l">{t("glossary.course")}</th>}
            <th className="th-l">
              {t("assessment_dashboard.student_outcome_page_list.page_header_title")}
            </th>
            <th className="th-s">
              {t("assessment_dashboard.student_outcome_page_list.date_assessed_header_title")}
            </th>
            <th className="th-s">
              {t("assessment_dashboard.student_outcome_page_list.available_points_header_title")}
            </th>
            <th className="th-xs">
              {t("assessment_dashboard.student_outcome_page_list.score_header_title")}
            </th>
          </tr>
        </thead>
        <ErrorBoundary minimal>
          <tbody className="body-xs">
            {data.map((page) => {
              const assessment = page.assessment;
              const outcomeScore = page.outcomeAssessment?.score;
              const dateAssessed = assessment?.created_at && tableDateFormat(assessment.created_at);
              const courseTitle = "course_title" in page ? page.course_title : undefined;

              return (
                <tr key={page.id}>
                  {showCourseColumn && <td>{courseTitle || "-"}</td>}
                  <td className="flex w-full justify-start gap-2">
                    {showCourseColumn ? (
                      <span className="block truncate">{page.title}</span>
                    ) : (
                      <Link className="block truncate" to={pageUrl(page.section_id, page.id)}>
                        {page.title}
                      </Link>
                    )}
                    {assessment && (
                      <Tooltip
                        text={
                          <div className="body-xs text-black-tint-40">{assessment.comment}</div>
                        }
                      >
                        <ChatIcon />
                      </Tooltip>
                    )}
                  </td>
                  <td>{dateAssessed || "-"}</td>
                  <td>{page.max_points ?? "-"}</td>
                  <td>{outcomeScore ? <ScoreIcon value={outcomeScore} /> : "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </ErrorBoundary>
      </table>
    </div>
  );
};
